import { useState } from 'react';
import SeoJsonLd from '../../components/seo/SeoJsonLd';

export default function Home() {
  const [calculatorArea, setCalculatorArea] = useState('');
  const [calculatorPackage, setCalculatorPackage] = useState('demolition');
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showResults, setShowResults] = useState(false);

  const packages = {
    demolition: { price: 30000, name: 'Демонтаж' },
    standard: { price: 55000, name: 'Стандарт' },
    comfort: { price: 75000, name: 'Комфорт' },
    business: { price: 95000, name: 'Бизнес' }
  };

  const calculatePrice = () => {
    const area = parseFloat(calculatorArea);
    if (area && area > 0) {
      // Use the selected package (calculatorPackage) to calculate price
      const selectedPackage = packages[calculatorPackage as keyof typeof packages];
      if (!selectedPackage) return '0';
      return (area * selectedPackage.price).toLocaleString('ru-RU');
    }
    return '0';
  };

  // Quiz state
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({
    area: '',
    spaceType: '',
    currentCondition: '',
    hasProject: '',
    package: '',
    timeline: ''
  });

  const quizQuestions = [
    {
      id: 'area',
      title: 'Какая площадь помещения?',
      type: 'input',
      placeholder: 'Введите площадь в м²',
      inputType: 'number'
    },
    {
      id: 'spaceType',
      title: 'Тип помещения',
      type: 'select',
      options: [
        { value: 'apartment', label: 'Квартира', multiplier: 1 },
        { value: 'office', label: 'Офис', multiplier: 1.2 },
        { value: 'retail', label: 'Торговое помещение', multiplier: 1.3 }
      ]
    },
    {
      id: 'currentCondition',
      title: 'Текущее состояние помещения',
      type: 'select',
      options: [
        { value: 'newbuild', label: 'Новостройка (черновая отделка)', multiplier: 1 },
        { value: 'renovation', label: 'Требует косметического ремонта', multiplier: 0.8 },
        { value: 'major', label: 'Требует капитального ремонта', multiplier: 1.1 }
      ]
    },
    {
      id: 'hasProject',
      title: 'Есть ли у вас готовый дизайн-проект?',
      type: 'select',
      options: [
        { value: 'no', label: 'Нет проекта, нужна помощь с планировкой', multiplier: 1.15 },
        { value: 'partial', label: 'Есть идеи, но нужна доработка', multiplier: 1.08 },
        { value: 'yes', label: 'Готовый дизайн-проект с чертежами', multiplier: 1 }
      ]
    },
    {
      id: 'package',
      title: 'Желаемый уровень отделки',
      type: 'select',
      options: [
        {
          value: 'demolition',
          label: 'Демонтаж — Снос и подготовка к ремонту',
          description:
            'Демонтаж старых покрытий, снос перегородок, вывоз мусора, подготовка помещения к ремонтным работам.',
          price: 30000
        },
        {
          value: 'standard',
          label: 'Стандарт — Чистовая + базовая комплектация',
          description:
            'Все работы Демонтаж + финишная отделка: покраска стен, укладка ламината/плитки, установка межкомнатных дверей, базовая сантехника, светильники, натяжные потолки. Готово к заселению.',
          price: 55000
        },
        {
          value: 'comfort',
          label: 'Комфорт — Премиум отделка, техника, декор',
          description:
            'Все работы Стандарт + премиальные материалы, многоуровневые потолки, встроенная мебель и техника, декоративные элементы, улучшенное освещение.',
          price: 75000
        },
        {
          value: 'business',
          label: 'Бизнес — Дизайн-отделка, авторский проект',
          description:
            'Максимальный уровень: дизайн-проект, эксклюзивные материалы, многоуровневые потолки, встроенная мебель и техника, декоративные элементы, умный дом, авторский дизайн.',
          price: 95000
        }
      ]
    },
    {
      id: 'timeline',
      title: 'Желаемые сроки завершения',
      type: 'select',
      options: [
        { value: 'urgent', label: 'До 45 дней (доплата 15%)', multiplier: 1.15 },
        { value: 'standard', label: '45-90 дней (стандартно)', multiplier: 1 },
        { value: 'flexible', label: 'Более 90 дней (скидка 5%)', multiplier: 0.95 }
      ]
    }
  ];

  const handleQuizAnswer = (questionId: string, value: string | string[]) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const calculateQuizPrice = () => {
    const area = parseFloat(quizAnswers.area);
    if (!area || area <= 0) return 0;

    // Base price from package
    const packageOption = quizQuestions[4].options.find(
      opt => opt.value === quizAnswers.package
    );
    if (!packageOption) return 0;

    let basePrice = packageOption.price * area;

    // Apply multipliers (fixed typo: multiplier, not multiplayer)
    const spaceTypeMultiplier =
      quizQuestions[1].options.find(
        opt => opt.value === quizAnswers.spaceType
      )?.multiplier || 1;
    const conditionMultiplier =
      quizQuestions[2].options.find(
        opt => opt.value === quizAnswers.currentCondition
      )?.multiplier || 1;
    const hasProjectMultiplier =
      quizQuestions[3].options.find(
        opt => opt.value === quizAnswers.hasProject
      )?.multiplier || 1;
    const timelineMultiplier =
      quizQuestions[5].options.find(
        opt => opt.value === quizAnswers.timeline
      )?.multiplier || 1;

    basePrice *=
      spaceTypeMultiplier *
      conditionMultiplier *
      hasProjectMultiplier *
      timelineMultiplier;

    return Math.round(basePrice);
  };

  const nextQuizStep = () => {
    if (quizStep < quizQuestions.length) {
      setQuizStep(quizStep + 1);
    }
  };

  const prevQuizStep = () => {
    if (quizStep > 1) {
      setQuizStep(quizStep - 1);
    }
  };

  const resetQuiz = () => {
    setQuizStep(0);
    setQuizAnswers({
      area: '',
      spaceType: '',
      currentCondition: '',
      hasProject: '',
      package: '',
      timeline: ''
    });
    setShowResults(false);
  };

  const startQuiz = () => {
    setQuizStep(1);
    setQuizAnswers({
      area: '',
      spaceType: '',
      currentCondition: '',
      hasProject: '',
      package: '',
      timeline: ''
    });
    setShowResults(false);
  };

  const editQuizAnswers = () => {
    setShowResults(false);
    setQuizStep(1);
  };

  const handleQuizSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Формируем детальное сообщение для WhatsApp с результатами квиза
      const hasProjectOption = quizQuestions[3].options.find(
        opt => opt.value === quizAnswers.hasProject
      );
      const packageOption = quizQuestions[4].options.find(
        opt => opt.value === quizAnswers.package
      );
      const spaceTypeOption = quizQuestions[1].options.find(
        opt => opt.value === quizAnswers.spaceType
      );
      const conditionOption = quizQuestions[2].options.find(
        opt => opt.value === quizAnswers.currentCondition
      );
      const timelineOption = quizQuestions[5].options.find(
        opt => opt.value === quizAnswers.timeline
      );

      const whatsappMessage = `🏠 ЗАЯВКА НА БЕСПЛАТНЫЙ ЗАМЕР И ТОЧНУЮ СМЕТУ

👤 Контактные данные:
• Имя: ${formData.name}
• Телефон: ${formData.phone}

📊 ПРЕДВАРИТЕЛЬНЫЙ РАСЧЕТ:
• Примерная стоимость: от ${calculateQuizPrice()
          .toLocaleString('ru-RU')} ₸
• Площадь: ${quizAnswers.area} м²
• Базовая стоимость за м²: от ${Math.round(
          calculateQuizPrice() / parseFloat(quizAnswers.area)
        ).toLocaleString('ru-RU')} ₸/м²

🏢 Параметры объекта:
• Тип помещения: ${spaceTypeOption?.label || 'Не указано'}
• Текущее состояние: ${conditionOption?.label || 'Не указано'}
• Дизайн-проект: ${hasProjectOption?.label || 'Не указано'}
• Уровень отделки: ${packageOption?.label || 'Не указано'}
• Желаемые сроки: ${timelineOption?.label || 'Не указано'}

📋 ТРЕБУЕТСЯ:
• Бесплатный выезд мастера для точного обмера
• Подготовка детальной сметы в течение 24 часов
• 3D-визуализацию и календарный план работ
• Консультацию по вариантам экономии

💰 АКЦИЯ: При заказе до конца месяца — замер + детальная планировка БЕСПЛАТНО!

⏰ Заявка отправлена: ${new Date().toLocaleString('ru-RU')}`;

      // Отправляем в WhatsApp
      const whatsappUrl = `https://wa.me/77002636781?text=${encodeURIComponent(
        whatsappMessage
      )}`;
      window.open(whatsappUrl, '_blank');

      // Показываем страницу с результатами
      setShowResults(true);
      setFormData({ name: '', phone: '' });

    } catch (error) {
      setSubmitMessage(
        'Ошибка отправки. Попробуйте еще раз или свяжитесь по телефону.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToQuiz = () => {
    const quizSection = document.getElementById('quiz-section');
    if (quizSection) {
      quizSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToExamples = () => {
    const examplesSection = document.getElementById('examples-section');
    if (examplesSection) {
      examplesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Обработчик клика по телефону
  const handlePhoneClick = () => {
    window.open('https://wa.me/77002636781', '_blank');
  };

  // Открытие WhatsApp
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      'Здравствуйте! Интересует ремонт помещения. Можете рассказать подробнее о ваших услугах?'
    );
    window.open(`https://wa.me/77002636781?text=${message}`, '_blank');
  };

  // Завершение квиза
  const completeQuiz = () => {
    setQuizStep(quizQuestions.length + 1);
  };

  const currentQuestion = quizQuestions[quizStep - 1];
  const isQuizComplete = quizStep > quizQuestions.length;
  const isQuizStarted = quizStep > 0;

  const seoData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'QOSTANAY Renovation',
    description:
      'Профессиональная ремонтная компания в Костанае. Ремонт квартир, офисов и коммерческих помещений.',
    url: import.meta.env.VITE_SITE_URL || 'https://example.com',
    telephone: '+77002636781',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Костанай',
      addressCountry: 'KZ'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '53.2138',
      longitude: '63.6345'
    },
    openingHours: 'Mo-Su 08:00-20:00',
    priceRange: 'от 35000-95000 ₸/м²',
    serviceArea: {
      '@type': 'City',
      name: 'Костанае'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Услуги ремонта',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Демонтаж - Снос и подготовка к ремонту',
            description:
              'Демонтаж старых покрытий, снос перегородок, вывоз мусора, подготовка помещения к ремонтным работам'
          },
          price: '30000',
          priceCurrency: 'KZT',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '30000',
            priceCurrency: 'KZT',
            unitCode: 'MTK'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Стандарт - Чистовая + базовая комплектация',
            description: 'Стандартный пакет включает чистовую отделку, базовую комплектацию'
          },
          price: '55000',
          priceCurrency: 'KZT',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '55000',
            priceCurrency: 'KZT',
            unitCode: 'MTK'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Комфорт - Премиум отделка, техника, декор',
            description:
              'Премиальный пакет с премиум отделкой, техникой и декором'
          },
          price: '75000',
          priceCurrency: 'KZT',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '75000',
            priceCurrency: 'KZT',
            unitCode: 'MTK'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Бизнес - Дизайн-отделка, авторский проект',
            description:
              'Максимальный пакет с дизайн-проектом и авторским проектом'
          },
          price: '95000',
          priceCurrency: 'KZT',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '95000',
            priceCurrency: 'KZT',
            unitCode: 'MTK'
          }
        }
      ]
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '180',
      bestRating: '5',
      worstRating: '1'
    },
    sameAs: ['https://wa.me/77002636781']
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoJsonLd data={seoData} />

      {/* Show Results Page */}
      {showResults && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="min-h-screen py-8 px-4">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="text-center mb-8">
                <div
                  className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center animate-bounce"
                  style={{ backgroundColor: '#22C55E' }}
                >
                  <i className="ri-check-line text-4xl text-white"></i>
                </div>
                <h1
                  className="text-3xl lg:text-4xl font-bold mb-4"
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    color: '#22262B'
                  }}
                >
                  ✅ ЗАЯВКА УСПЕШНО ОТПРАВЛЕНА!
                </h1>
                <p className="text-xl text-gray-700 mb-6">
                  Ваш расчет отправлен в WhatsApp. Мастер ответит в течение 5 минут.
                </p>
              </div>

              {/* Results Summary */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-3xl p-8 mb-8 border-2 border-green-200">
                <h2
                  className="text-2xl font-bold mb-4 text-center"
                  style={{ color: '#22262B' }}
                >
                  📊 ВАША ПРЕДВАРИТЕЛЬНАЯ СМЕТА
                </h2>
                <div className="grid lg:grid-cols-2 gap-6">
                  <div>
                    <div
                      className="text-4xl font-bold mb-2"
                      style={{ color: '#D69A00' }}
                    >
                      от {calculateQuizPrice().toLocaleString('ru-RU')} ₸
                    </div>
                    <p className="text-lg font-semibold text-gray-700 mb-4">
                      {quizAnswers.area} м² •{' '}
                      {quizQuestions[4].options
                        .find(opt => opt.value === quizAnswers.package)
                        ?.label.split(' — ')[0]}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Базовая стоимость за м²:</span>
                      <span className="font-semibold">
                        от {Math.round(calculateQuizPrice() / parseFloat(quizAnswers.area)).toLocaleString('ru-RU')} ₸
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Площадь:</span>
                      <span className="font-semibold">{quizAnswers.area} м²</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Notice */}
              <div className="bg-blue-50 rounded-2xl p-6 mb-8 border-2 border-blue-200">
                <div className="flex items-start space-x-4">
                  <i className="ri-information-line text-2xl text-blue-600 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-blue-800 mb-2">
                      📱 Что происходит дальше?
                    </h3>
                    <ul className="space-y-2 text-blue-700">
                      <li>• В WhatsApp вы получите предварительный расчет</li>
                      <li>• Мастер свяжется с вами в течение 5 минут</li>
                      <li>• Точная стоимость будет определена после осмотра на месте</li>
                      <li>• Финальная цена зависит от ваших пожеланий и особенностей объекта</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-yellow-50 rounded-2xl p-6 mb-8 border-2 border-yellow-200">
                <h3 className="text-lg font-bold text-yellow-800 mb-4">
                  🎯 СЛЕДУЮЩИЕ ШАГИ:
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-600 flex items-center justify-center">
                      <span className="text-white font-bold">1</span>
                    </div>
                    <h4 className="font-semibold mb-2">Звонок мастера</h4>
                    <p className="text-sm text-gray-600">В течение 5 минут для уточнения деталей</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-600 flex items-center justify-center">
                      <span className="text-white font-bold">2</span>
                    </div>
                    <h4 className="font-semibold mb-2">Выезд на объект</h4>
                    <p className="text-sm text-gray-600">Бесплатный замер и консультация</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-600 flex items-center justify-center">
                      <span className="text-white font-bold">3</span>
                    </div>
                    <h4 className="font-semibold mb-2">Точная смета</h4>
                    <p className="text-sm text-gray-600">Готова в течение 24 часов</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleWhatsAppClick}
                  className="px-8 py-4 rounded-2xl font-bold text-white bg-green-600 hover:bg-green-700 transition-all cursor-pointer flex items-center justify-center space-x-2"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  <span>ОТКРЫТЬ WHATSAPP</span>
                </button>
                <button
                  onClick={resetQuiz}
                  className="px-8 py-4 rounded-2xl font-bold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-all cursor-pointer"
                >
                  РАССЧИТАТЬ ЗАНОВО
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with Logo */}
      <header className="bg-white shadow-sm py-3 px-4 lg:py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-xl lg:text-2xl font-bold text-gray-800 cursor-pointer hover:text-yellow-600 transition-colors"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              QOSTANAY Renovation
            </button>
          </div>
          <div className="flex items-center space-x-2 lg:space-x-6">
            <button
              onClick={handleWhatsAppClick}
              className="flex items-center text-gray-700 hover:text-yellow-600 transition-colors cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg lg:text-xl mr-1 lg:mr-2"></i>
              <span
                className="text-sm lg:text-base"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                +7 (700) 263-67-81
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section - Мобильная адаптация */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center px-4"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 11, 11, 0.5), rgba(11, 11, 11, 0.5)), url('https://public.readdy.ai/ai/img_res/7bc6ca55454f096424d6a5de1ef60813.jpg')`
        }}
      >
        <div className="w-full max-w-6xl mx-auto text-center text-white">
          {/* 1. Крупный заголовок - адаптирован под мобильные */}
          <h1
            className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-4 lg:mb-6 leading-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Качественный ремонт с фиксированнной сметой
            <br className="hidden sm:block" />
            <span className="block sm:inline" style={{ color: '#D69A00' }}>
              и юридическими гарантиями
            </span>
          </h1>

          {/* Подзаголовок - адаптирован */}
          <p
            className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 lg:mb-8 max-w-5xl mx-auto font-medium px-2"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Экономия до 32% от рынка • Готовая смета за 24 часа • Гарантия 12 месяцев
          </p>

          {/* 2. Кнопки действия - мобильная адаптация */}
          <div className="flex flex-col gap-3 lg:gap-4 justify-center items-center mb-8 lg:mb-12 px-2">
            <button
              onClick={scrollToQuiz}
              className="w-full sm:w-auto px-8 lg:px-12 py-4 lg:py-6 rounded-2xl text-lg lg:text-xl font-bold transition-all shadow-2xl hover:shadow-3xl cursor-pointer whitespace-nowrap uppercase transform hover:scale-105"
              style={{
                backgroundColor: '#D69A00',
                color: 'white',
                fontFamily: 'Manrope, sans-serif',
                border: '3px solid #FFD700'
              }}
            >
              🔥 РАССЧИТАТЬ СМЕТУ
            </button>
            <button
              onClick={handleWhatsAppClick}
              className="w-full sm:w-auto px-6 lg:px-8 py-4 lg:py-6 rounded-2xl text-base lg:text-lg font-semibold bg-green-600 hover:bg-green-700 text-white transition-all cursor-pointer whitespace-nowrap flex items-center justify-center space-x-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <i className="ri-whatsapp-line text-lg lg:text-xl"></i>
              <span>НАПИСАТЬ В WHATSAPP</span>
            </button>
          </div>

          {/* 3. УТП в иконках - мобильная сетка */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-8 lg:mb-10 max-w-5xl mx-auto">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 lg:p-5 border-2 border-green-300">
              <div className="flex items-center space-x-2 lg:space-x-3">
                <i className="ri-shield-check-line text-xl lg:text-2xl text-green-600"></i>
                <div>
                  <div className="font-bold text-green-800 text-base lg:text-lg">
                    180+ объектов
                  </div>
                  <div className="text-green-700 text-xs lg:text-sm">
                    Подтвержденный опыт
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 lg:p-5 border-2 border-blue-300">
              <div className="flex items-center space-x-2 lg:space-x-3">
                <i className="ri-file-text-line text-xl lg:text-2xl text-blue-600"></i>
                <div>
                  <div className="font-bold text-blue-800 text-base lg:text-lg">
                    Полный договор
                  </div>
                  <div className="text-blue-700 text-xs lg:text-sm">
                    Юридические гарантии
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 lg:p-5 border-2 border-purple-300">
              <div className="flex items-center space-x-2 lg:space-x-3">
                <i className="ri-money-dollar-circle-line text-xl lg:text-2xl text-purple-600"></i>
                <div>
                  <div className="font-bold text-purple-800 text-base lg:text-lg">
                    Без переплат
                  </div>
                  <div className="text-purple-700 text-xs lg:text-sm">
                    Фиксированный бюджет
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 lg:p-5 border-2 border-orange-300 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center justify-center sm:justify-start space-x-2 lg:space-x-3">
                <i className="ri-time-line text-xl lg:text-2xl text-orange-600"></i>
                <div>
                  <div className="font-bold text-orange-800 text-base lg:text-lg">
                    24 часа
                  </div>
                  <div className="text-orange-700 text-xs lg:text-sm">
                    Готовая смета
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Блок срочности - мобильная адаптация */}
          <div className="bg-red-600/95 rounded-2xl p-4 lg:p-6 mb-6 lg:mb-8 max-w-4xl mx-auto border-3 border-red-400 shadow-2xl">
            <div className="flex flex-col lg:flex-row items-center justify-center space-y-3 lg:space-y-0 lg:space-x-4 text-white">
              <i className="ri-alarm-warning-line text-2xl lg:text-3xl animate-pulse text-yellow-300"></i>
              <div className="text-center">
                <div className="font-bold text-lg lg:text-2xl mb-1 lg:mb-2">
                  ⚡ С начала следующего месяца цены вырастут на 15%
                </div>
                <div className="text-sm lg:text-lg mb-2 lg:mb-3">
                  Успейте зафиксировать текущую цену до конца месяца
                </div>
                <button
                  onClick={scrollToQuiz}
                  className="px-6 lg:px-8 py-2 lg:py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-xl transition-all cursor-pointer text-sm lg:text-base"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  УСПЕТЬ ДО ПОДОРОЖАНИЯ
                </button>
              </div>
            </div>
          </div>

          {/* 5. Подвал первого экрана */}
          <div className="text-center">
            <button
              onClick={scrollToExamples}
              className="inline-flex flex-col items-center text-white/80 hover:text-white transition-colors cursor-pointer animate-bounce"
            >
              <span
                className="text-xs lg:text-sm mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Посмотрите примеры работ и цены
              </span>
              <i className="ri-arrow-down-line text-xl lg:text-2xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Quick Calculator Section - мобильная адаптация */}
      <section
        id="quiz-section"
        className="py-12 lg:py-20 px-4"
        style={{ backgroundColor: '#F8F9FA' }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 lg:mb-12">
            <h2
              className="text-2xl lg:text-3xl xl:text-4xl font-bold mb-3 lg:mb-4"
              style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
            >
              Быстрый расчет стоимости ремонта
            </h2>
            <p
              className="text-lg lg:text-xl"
              style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}
            >
              Ответьте на несколько вопросов и получите предварительную смету
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-8">
            {!isQuizStarted ? (
              <div className="text-center py-8 lg:py-12">
                <div
                  className="w-16 lg:w-20 h-16 lg:h-20 mx-auto mb-4 lg:mb-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#D69A00' }}
                >
                  <i className="ri-calculator-line text-2xl lg:text-3xl text-white"></i>
                </div>
                <h3
                  className="text-xl lg:text-2xl font-bold mb-3 lg:mb-4"
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    color: '#22262B'
                  }}
                >
                  Готовы узнать стоимость?
                </h3>
                <p className="text-base lg:text-lg text-gray-600 mb-6 lg:mb-8 px-2">
                  Пройдите короткий квиз из 5 вопросов и получите
                  предварительный расчет стоимости ремонта
                </p>
                <button
                  onClick={startQuiz}
                  className="w-full sm:w-auto px-8 lg:px-12 py-3 lg:py-4 rounded-2xl font-semibold text-white cursor-pointer whitespace-nowrap hover:shadow-lg transition-all"
                  style={{ backgroundColor: '#D69A00' }}
                >
                  НАЧАТЬ РАСЧЕТ
                </button>
              </div>
            ) : !isQuizComplete ? (
              <>
                {/* Progress Bar */}
                <div className="mb-6 lg:mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium" style={{ color: '#22262B' }}>
                      Вопрос {quizStep} из {quizQuestions.length}
                    </span>
                    <span className="text-sm font-medium" style={{ color: '#D69A00' }}>
                      {Math.round((quizStep / quizQuestions.length) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        backgroundColor: '#D69A00',
                        width: `${(quizStep / quizQuestions.length) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>

                {/* Question */}
                <div className="mb-6 lg:mb-8">
                  <h3
                    className="text-xl lg:text-2xl font-bold mb-4 lg:mb-6"
                    style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                  >
                    {currentQuestion.title}
                  </h3>

                  {currentQuestion.type === 'input' && (
                    <div>
                      <input
                        type={currentQuestion.inputType || 'text'}
                        value={
                          quizAnswers[
                            currentQuestion.id as keyof typeof quizAnswers
                          ] as string
                        }
                        onChange={e =>
                          handleQuizAnswer(currentQuestion.id, e.target.value)
                        }
                        className="w-full px-4 lg:px-6 py-3 lg:py-4 border-2 border-gray-200 rounded-2xl text-base lg:text-lg focus:border-yellow-500 focus:outline-none"
                        placeholder={currentQuestion.placeholder}
                      />
                    </div>
                  )}

                  {currentQuestion.type === 'select' && (
                    <div className="grid gap-3">
                      {currentQuestion.options?.map((option, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() =>
                            handleQuizAnswer(currentQuestion.id, option.value)
                          }
                          className={`p-3 lg:p-4 rounded-2xl border-2 text-left transition-all cursor-pointer ${
                            quizAnswers[
                              currentQuestion.id as keyof typeof quizAnswers
                            ] === option.value
                              ? 'border-yellow-500 bg-yellow-50'
                              : 'border-gray-200 hover:border-yellow-300'
                          }`}
                        >
                          <div>
                            <div className="font-semibold mb-1 lg:mb-2 text-sm lg:text-base">
                              {option.label}
                            </div>
                            {option.description && (
                              <div className="text-xs lg:text-sm text-gray-600 leading-relaxed">
                                {option.description}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center gap-4">
                  <button
                    type="button"
                    onClick={prevQuizStep}
                    disabled={quizStep === 1}
                    className={`px-4 lg:px-6 py-2 lg:py-3 rounded-2xl font-semibold cursor-pointer whitespace-nowrap text-sm lg:text-base ${
                      quizStep === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ← Назад
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (quizStep === quizQuestions.length) {
                        completeQuiz();
                      } else {
                        nextQuizStep();
                      }
                    }}
                    disabled={
                      !quizAnswers[
                        currentQuestion.id as keyof typeof quizAnswers
                      ] ||
                      (Array.isArray(
                        quizAnswers[
                          currentQuestion.id as keyof typeof quizAnswers
                        ]
                      ) &&
                        (quizAnswers[
                          currentQuestion.id as keyof typeof quizAnswers
                        ] as string[]).length === 0)
                    }
                    className={`px-6 lg:px-8 py-2 lg:py-3 rounded-2xl font-semibold cursor-pointer whitespace-nowrap text-sm lg:text-base ${
                      !quizAnswers[
                        currentQuestion.id as keyof typeof quizAnswers
                      ] ||
                      (Array.isArray(
                        quizAnswers[
                          currentQuestion.id as keyof typeof quizAnswers
                        ] as string[]
                      ) && (quizAnswers[
                          currentQuestion.id as keyof typeof quizAnswers
                        ] as string[]).length === 0)
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'text-white'
                    }`}
                    style={{
                      backgroundColor:
                        !quizAnswers[
                          currentQuestion.id as keyof typeof quizAnswers
                        ] ||
                        (Array.isArray(
                          quizAnswers[
                            currentQuestion.id as keyof typeof quizAnswers
                          ]
                        ) &&
                          (quizAnswers[
                            currentQuestion.id as keyof typeof quizAnswers
                          ] as string[]).length === 0)
                          ? '#E5E7EB'
                          : '#D69A00'
                    }}
                  >
                    {quizStep === quizQuestions.length
                      ? 'Рассчитать смету'
                      : 'Далее →'}
                  </button>
                </div>
              </>
            ) : (
              /* Quiz Results - мобильная адаптация */
              <div className="text-center">
                <div className="mb-6 lg:mb-8">
                  <div
                    className="w-20 lg:w-24 h-20 lg:h-24 mx-auto mb-4 lg:mb-6 rounded-full flex items-center justify-center animate-bounce"
                    style={{ backgroundColor: '#D69A00' }}
                  >
                    <i className="ri-trophy-line text-3xl lg:text-4xl text-white"></i>
                  </div>
                  <h3
                    className="text-2xl lg:text-4xl font-bold mb-3 lg:mb-4"
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      color: '#22262B'
                    }}
                  >
                    🎉 ВАША ПЕРСОНАЛЬНАЯ СМЕТА ГОТОВА!
                  </h3>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-3xl p-4 lg:p-8 mb-4 lg:mb-6 border-2 border-green-200">
                    <div
                      className="text-4xl lg:text-6xl font-bold mb-2 lg:mb-3"
                      style={{ color: '#D69A00' }}
                    >
                      ИНДИВИДУАЛЬНЫЙ РАСЧЕТ ГОТОВ
                    </div>
                    <p className="text-lg lg:text-2xl font-semibold text-gray-700 mb-1 lg:mb-2">
                      {quizAnswers.area} м² •{' '}
                      {quizQuestions[4].options
                        .find(
                          opt => opt.value === quizAnswers.package
                        )
                        ?.label.split(' — ')[0]}
                    </p>
                    <div
                      className="inline-flex items-center px-4 lg:px-6 py-2 lg:py-3 rounded-full text-base lg:text-lg font-bold text-white mt-2 lg:mt-3"
                      style={{ backgroundColor: '#22C55E' }}
                    >
                      📱 Точная стоимость в WhatsApp после отправки заявки
                    </div>
                  </div>

                  {/* Enhanced Urgency Block - мобильная адаптация */}
                  <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-3xl p-4 lg:py-6 mb-6 lg:mb-8 text-white border-3 border-red-400 shadow-2xl">
                    <div className="flex flex-col lg:flex-row items-center justify-center space-y-2 lg:space-y-0 lg:space-x-3 mb-3 lg:mb-4">
                      <i className="ri-alarm-warning-line text-3xl lg:text-4xl animate-pulse text-yellow-300"></i>
                      <div className="text-center lg:text-left">
                        <div className="font-bold text-2xl lg:text-3xl">
                          ⚡ ПОСЛЕДНИЙ ШАНС!
                        </div>
                        <div className="text-lg lg:text-xl">
                          Эта цена действует только до конца месяца
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-800/60 rounded-2xl p-3 lg:p-5">
                      <div className="text-center">
                        <div className="text-xl lg:text-2xl font-bold mb-2 lg:mb-3">
                          🔥 ВНИМАНИЕ! Через 24 часа:
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:gap-4 text-sm lg:text-lg">
                          <div>❌ Цены вырастут на 15%</div>
                          <div>❌ Бесплатный замер + смета закончится</div>
                          <div>❌ Очередь увеличится на 2 недели</div>
                          <div>❌ Скидка на материалы исчезнет</div>
                          <div className="mt-3 lg:mt-4 p-3 lg:p-4 bg-yellow-500 rounded-2xl text-black font-bold text-lg lg:text-xl">
                            ⏰ ОСТАЛОСЬ ВСЕГО 3 МЕСТА В СЛЕДУЮЩЕМ МЕСЯЦЕ!
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MEGA Enhanced Contact Form - мобильная адаптация */}
                  <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-3xl p-4 lg:p-8 mb-4 lg:mb-6 border-4 border-yellow-300 shadow-2xl">
                    <div className="mb-4 lg:mb-6 text-center">
                      <h4
                        className="text-2xl lg:text-4xl font-bold mb-2 lg:mb-4"
                        style={{
                          fontFamily: 'Manrope, sans-serif',
                          color: '#D69A00'
                        }}
                      >
                        🎯 ПОЛУЧИТЕ ТОЧНУЮ СТОИМОСТЬ В WHATSAPP!
                      </h4>
                      <div className="bg-green-600 text-white rounded-2xl p-3 lg:p-4 mb-3 lg:mb-4">
                        <p className="text-lg lg:text-2xl font-bold mb-1 lg:mb-2">
                          ✅ ПОСЛЕ ОТПРАВКИ ЗАЯВКИ:
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 lg:gap-2 text-sm lg:lg:text-lg">
                          <div>• Предварительная стоимость в WhatsApp</div>
                          <div>• Мастер приедет СЕГОДНЯ</div>
                          <div>• Точная смета после осмотра</div>
                          <div>• Замер + планировка БЕСПЛАТНО</div>
                        </div>
                      </div>

                      <div className="bg-blue-50 rounded-2xl p-3 lg:p-4 mb-3 lg:mb-4 border-2 border-blue-200">
                        <div className="flex items-start space-x-3">
                          <i className="ri-information-line text-xl text-blue-600 mt-1"></i>
                          <div className="text-left">
                            <p className="text-base lg:text-lg font-bold text-blue-800 mb-1">
                              ℹ️ ВАЖНО ЗНАТЬ:
                            </p>
                            <p className="text-sm lg:text-base text-blue-700">
                              В WhatsApp вы получите предварительный расчет. Точная стоимость будет определена после осмотра на месте и обсуждения ваших пожеланий.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-red-600 text-white rounded-2xl p-3 lg:p-4 mb-3 lg:mb-4">
                        <p className="text-lg lg:text-xl font-bold">⚠️ ЕСЛИ НЕ ОТПРАВИТЕ ДО КОНЦА МЕСЯЦА:</p>
                        <p className="text-base lg:text-lg">
                          С начала следующего месяца эта же работа будет стоить на 15% дороже!
                        </p>
                      </div>

                      <form onSubmit={handleQuizSubmit} className="space-y-4 lg:space-y-6">
                        <div className="text-center mb-3 lg:mb-4">
                          <p
                            className="text-xl lg:text-2xl font-bold text-gray-800"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                          >
                            👆 Оставьте контакты для получения расчета
                          </p>
                          <p className="text-base lg:text-lg text-gray-700 mt-2">
                            Отправим предварительный расчёт в WhatsApp и договоримся о встрече
                          </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                          <input
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={e =>
                              setFormData({ ...formData, name: e.target.value })
                            }
                            className="px-4 lg:px-6 py-4 lg:py-5 border-3 border-yellow-400 rounded-2xl text-base lg:text-lg focus:border-yellow-600 focus:outline-none font-medium text-center"
                            placeholder="Ваше имя"
                          />
                          <input
                            type="tel"
                            name="phone"
                            required
                            value={formData.phone}
                            onChange={e =>
                              setFormData({ ...formData, phone: e.target.value })
                            }
                            className="px-4 lg:px-6 py-4 lg:py-5 border-3 border-yellow-400 rounded-2xl text-base lg:text-lg focus:border-yellow-600 focus:outline-none font-medium text-center"
                            placeholder="Телефон / WhatsApp"
                          />
                        </div>

                        <div className="space-y-4">
                          {/* Edit Answers Button */}
                          <button
                            type="button"
                            onClick={editQuizAnswers}
                            className="w-full py-3 rounded-2xl font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-all cursor-pointer text-base"
                          >
                            ✏️ ИЗМЕНИТЬ ОТВЕТЫ
                          </button>

                          {/* Main Submit Button */}
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full py-6 rounded-2xl font-bold text-white bg-green-600 hover:bg-green-700 transition-all cursor-pointer text-xl shadow-2xl border-4 border-green-400"
                            style={{
                              background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)',
                              boxShadow: '0 10px 30px rgba(34, 197, 94, 0.4)'
                            }}
                          >
                            {isSubmitting ? (
                              <div className="flex items-center justify-center space-x-2">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                <span>ОТПРАВЛЯЕМ...</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center space-x-2">
                                <i className="ri-whatsapp-line text-2xl"></i>
                                <span>📱 ОТПРАВИТЬ ЗАЯВКУ В WHATSAPP</span>
                              </div>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                  {/* Final Security Block */}
                  <div className="bg-gray-900 text-white rounded-3xl p-4 lg:p-6 mb-4 lg:mb-6">
                    <div className="text-center">
                      <div className="flex flex-col lg:flex-row items-center justify-center space-y-2 lg:space-y-0 lg:space-x-3 mb-2 lg:mb-3">
                        <i className="ri-shield-check-line text-3xl lg:text-4xl text-green-400"></i>
                        <div>
                          <div className="font-bold text-xl lg:text-2xl">
                            🛡️ РАБОТАЕМ БЕЗ РИСКОВ
                          </div>
                          <div className="text-base lg:text-lg opacity-90">
                            Гарантия возврата 100% предоплаты
                          </div>
                        </div>
                      </div>
                      <p className="text-sm lg:text-base opacity-75">
                        Если результат не понравится — вернём деньги без вопросов
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Examples Section - мобильная адаптация */}
      <section
        id="examples-section"
        className="py-12 lg:py-20 px-4"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <h2
              className="text-2xl lg:text-3xl xl:text-4xl font-bold text-center mb-4 lg:mb-6"
              style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
            >
              Возможные варианты работ
            </h2>
            <p
              className="text-lg lg:text-xl mb-6 lg:mb-8 max-w-4xl mx-auto"
              style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}
            >
              Более 180 завершенных проектов в Казахстане с 2019 года
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12 lg:mb-16">
            {/* Проект 1 - Квартира Демонтаж */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Kazakhstan%20apartment%20renovation%20demolition%20stage%20showing%20exposed%20walls%20concrete%20floors%20bare%20electrical%20wiring%20plumbing%20pipes%20construction%20materials%20scattered%20around%20unfinished%20raw%20building%20work%20in%20Kostanay%20residential%20building&width=400&height=300&seq=demolition-raw-stage1&orientation=landscape"
                  alt="Квартира на стадии Демонтаж ремонта"
                  className="w-full h-48 lg:h-56 object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-green-600 text-white px-2 py-1 rounded-lg text-xs font-bold">
                  ДЕМОНТАЖ ПАКЕТ
                </div>
              </div>
              <div className="p-4 lg:p-6">
                <h3
                  className="font-bold text-lg lg:text-xl mb-2 lg:mb-4"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                >
                  2-комнатная квартира
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Площадь:</span>
                    <span className="font-semibold">65 м²</span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Пакет:</span>
                    <span className="font-semibold">
                      Демонтаж - от 30,000₸/м²
                    </span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Срок:</span>
                    <span className="font-semibold">2.5 месяца</span>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm lg:text-base">
                      Общая стоимость:
                    </span>
                    <span className="text-xl lg:text-2xl font-bold" style={{ color: '#D69A00' }}>
                      от 1,950,000 ₸
                    </span>
                  </div>
                </div>
                <div className="mt-3 text-xs lg:text-sm text-gray-500">
                  Включено: демонтаж, черновые работы, электрика, сантехника, стяжка, штукатурка
                </div>
              </div>
            </div>

            {/* Проект 2 - Офис Стандарт */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Kazakhstan%20office%20interior%20after%20standard%20renovation%20package%20in%20Kostanay%20city%20with%20professional%20workstations%20white%20walls%20laminate%20flooring%20basic%20LED%20lighting%20suspended%20ceiling%20conference%20area%20ready%20for%20work&width=400&height=300&seq=office-standard-finished2&orientation=landscape"
                  alt="Офис после Стандарт ремонта"
                  className="w-full h-48 lg:h-56 object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-blue-600 text-white px-2 py-1 rounded-lg text-xs font-bold">
                  СТАНДАРТ ПАКЕТ
                </div>
              </div>
              <div className="p-4 lg:p-6">
                <h3
                  className="font-bold text-lg lg:text-xl mb-2 lg:mb-4"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                >
                  Офисное помещение
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Площадь:</span>
                    <span className="font-semibold">80 м²</span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Пакет:</span>
                    <span className="font-semibold">
                      Стандарт - от 55,000₸/м²
                    </span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Срок:</span>
                    <span className="font-semibold">1.5 месяца</span>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm lg:text-base">
                      Общая стоимость:
                    </span>
                    <span className="text-xl lg:text-2xl font-bold" style={{ color: '#D69A00' }}>
                      от 4,400,000 ₸
                    </span>
                  </div>
                </div>
                <div className="mt-3 text-xs lg:text-sm text-gray-500">
                  Включено: все работы Демонтаж + чистовая отделка: покраска стен, укладка ламината/плитки, установка межкомнатных дверей, базовая сантехника и светильники, натяжные потолки. Готово к заселению.
                </div>
              </div>
            </div>

            {/* Проект 3 - Кухня Комфорт */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Premium%20Kazakhstan%20kitchen%20interior%20after%20comfort%20renovation%20package%20with%20luxury%20cabinets%20granite%20countertops%20built-in%20appliances%20multi-level%20ceilings%20decorative%20lighting%20beautiful%20tile%20backsplash%20in%20Kostanay%20apartment&width=400&height=300&seq=kitchen-comfort-premium3&orientation=landscape"
                  alt="Кухня после Комфорт ремонта"
                  className="w-full h-48 lg:h-56 object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-purple-600 text-white px-2 py-1 rounded-lg text-xs font-bold">
                  КОМФОРТ ПАКЕТ
                </div>
              </div>
              <div className="p-4 lg:p-6">
                <h3
                  className="font-bold text-lg lg:text-xl mb-2 lg:mb-4"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                >
                  Кухня премиум
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Площадь:</span>
                    <span className="font-semibold">18 м²</span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Пакет:</span>
                    <span className="font-semibold">
                      Комфорт - от 75,000₸/м²
                    </span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Срок:</span>
                    <span className="font-semibold">1.5 месяца</span>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm lg:text-base">
                      Общая стоимость:
                    </span>
                    <span className="text-xl lg:text-2xl font-bold" style={{ color: '#D69A00' }}>
                      от 1,350,000 ₸
                    </span>
                  </div>
                </div>
                <div className="mt-3 text-xs lg:text-sm text-gray-500">
                  Включено: премиальные материалы, встроенная мебель, многоуровневые потолки
                </div>
              </div>
            </div>

            {/* Проект 4 - Гостиная Бизнес */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20Kazakhstan%20living%20room%20interior%20after%20business%20renovation%20package%20with%20exclusive%20designer%20furniture%20custom%20built-in%20storage%20smart%20home%20system%20artistic%20ceiling%20design%20premium%20materials%20author%20design%20project%20in%20elegant%20Kostanay%20apartment&width=400&height=300&seq=living-business-luxury4&orientation=landscape"
                  alt="Гостиная после Бизнес ремонта"
                  className="w-full h-48 lg:h-56 object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-yellow-600 text-white px-2 py-1 rounded-lg text-xs font-bold">
                  БИЗНЕС ПАКЕТ
                </div>
              </div>
              <div className="p-4 lg:p-6">
                <h3
                  className="font-bold text-lg lg:text-xl mb-2 lg:mb-4"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                >
                  Гостиная люкс
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Площадь:</span>
                    <span className="font-semibold">35 м²</span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Пакет:</span>
                    <span className="font-semibold">
                      Бизнес - от 95,000₸/м²
                    </span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Срок:</span>
                    <span className="font-semibold">2.5 месяца</span>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm lg:text-base">
                      Общая стоимость:
                    </span>
                    <span className="text-xl lg:text-2xl font-bold" style={{ color: '#D69A00' }}>
                      от 3,325,000 ₸
                    </span>
                  </div>
                </div>
                <div className="mt-3 text-xs lg:text-sm text-gray-500">
                  Включено: дизайн-проект, эксклюзивные материалы, умный дом, авторский дизайн
                </div>
              </div>
            </div>

            {/* Проект 5 - Ванная Стандарт */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20Kazakhstan%20bathroom%20interior%20after%20standard%20renovation%20with%20white%20ceramic%20tiles%20shower%20cabin%20modern%20sink%20toilet%20basic%20fixtures%20functional%20design%20good%20lighting%20waterproofing%20completed%20in%20Kostanay%20apartment&width=400&height=300&seq=bathroom-standard-clean5&orientation=landscape"
                  alt="Ванная после Стандарт ремонта"
                  className="w-full h-48 lg:h-56 object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-blue-600 text-white px-2 py-1 rounded-lg text-xs font-bold">
                  СТАНДАРТ ПАКЕТ
                </div>
              </div>
              <div className="p-4 lg:p-6">
                <h3
                  className="font-bold text-lg lg:text-xl mb-2 lg:mb-4"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                >
                  Совмещенный санузел
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Площадь:</span>
                    <span className="font-semibold">8 м²</span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Пакет:</span>
                    <span className="font-semibold">
                      Стандарт - от 55,000₸/м²
                    </span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Срок:</span>
                    <span className="font-semibold">3 недели</span>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm lg:text-base">
                      Общая стоимость:
                    </span>
                    <span className="text-xl lg:text-2xl font-bold" style={{ color: '#D69A00' }}>
                      от 440,000 ₸
                    </span>
                  </div>
                </div>
                <div className="mt-3 text-xs lg:text-sm text-gray-500">
                  Включено: гидроизоляция, плитка, сантехника, вентиляция, освещение
                </div>
              </div>
            </div>

            {/* Проект 6 - Спальня Комфорт */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20Kazakhstan%20bedroom%20interior%20after%20comfort%20renovation%20with%20premium%20materials%20built-in%20wardrobe%20decorative%20ceiling%20modern%20lighting%20quality%20finishes%20cozy%20atmosphere%20in%20Kostanay%20apartment%20bedroom%20design&width=400&height=300&seq=bedroom-comfort-cozy6&orientation=landscape"
                  alt="Спальня после Комфорт ремонта"
                  className="w-full h-48 lg:h-56 object-cover object-top"
                />
                <div className="absolute top-3 left-3 bg-purple-600 text-white px-2 py-1 rounded-lg text-xs font-bold">
                  КОМФОРТ ПАКЕТ
                </div>
              </div>
              <div className="p-4 lg:p-6">
                <h3
                  className="font-bold text-lg lg:text-xl mb-2 lg:mb-4"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                >
                  Спальня комфорт
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Площадь:</span>
                    <span className="font-semibold">20 м²</span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Пакет:</span>
                    <span className="font-semibold">
                      Комфорт - от 75,000₸/м²
                    </span>
                  </div>
                  <div className="flex justify-between text-sm lg:text-base">
                    <span className="text-gray-600">Срок:</span>
                    <span className="font-semibold">1.5 месяца</span>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm lg:text-base">
                      Общая стоимость:
                    </span>
                    <span className="text-xl lg:text-2xl font-bold" style={{ color: '#D69A00' }}>
                      от 1,500,000 ₸
                    </span>
                  </div>
                </div>
                <div className="mt-3 text-xs lg:text-sm text-gray-500">
                  Включено: встроенная мебель, премиальные материалы, улучшенное освещение
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Package Comparison Section */}
      <section className="py-12 lg:py-20 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <h2
              className="text-2xl lg:text-3xl xl:text-4xl font-bold mb-4 lg:mb-6"
              style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
            >
              Сравнение пакетов услуг
            </h2>
            <p
              className="text-lg lg:text-xl max-w-4xl mx-auto mb-8"
              style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}
            >
              Выберите оптимальный уровень ремонта под ваши потребности и бюджет
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left p-6 bg-gray-50">
                      <div
                        className="text-lg font-bold text-gray-800"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        Что включено
                      </div>
                    </th>
                    <th className="text-center p-6 bg-green-50 border-l border-gray-200">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 mb-3 rounded-full flex items-center justify-center bg-green-600">
                          <i className="ri-hammer-line text-xl text-white"></i>
                        </div>
                        <div
                          className="text-xl font-bold text-green-800 mb-1"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          Демонтаж
                        </div>
                        <div className="text-2xl font-bold text-green-600">
                          от 30,000₸
                        </div>
                        <div className="text-sm text-green-600">за м²</div>
                      </div>
                    </th>
                    <th className="text-center p-6 bg-blue-50 border-l border-gray-200 relative">
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                          ПОПУЛЯРНЫЙ
                        </div>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 mb-3 rounded-full flex items-center justify-center bg-blue-600">
                          <i className="ri-home-4-line text-xl text-white"></i>
                        </div>
                        <div
                          className="text-xl font-bold text-blue-800 mb-1"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          Стандарт
                        </div>
                        <div className="text-2xl font-bold text-blue-600">
                          от 55,000₸
                        </div>
                        <div className="text-sm text-blue-600">за м²</div>
                      </div>
                    </th>
                    <th className="text-center p-6 bg-purple-50 border-l border-gray-200">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 mb-3 rounded-full flex items-center justify-center bg-purple-600">
                          <i className="ri-vip-crown-line text-xl text-white"></i>
                        </div>
                        <div
                          className="text-xl font-bold text-purple-800 mb-1"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          Комфорт
                        </div>
                        <div className="text-2xl font-bold text-purple-600">
                          от 75,000₸
                        </div>
                        <div className="text-sm text-purple-600">за м²</div>
                      </div>
                    </th>
                    <th className="text-center p-6 bg-yellow-50 border-l border-gray-200">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 mb-3 rounded-full flex items-center justify-center bg-yellow-600">
                          <i className="ri-vip-crown-2-line text-xl text-white"></i>
                        </div>
                        <div
                          className="text-xl font-bold text-yellow-800 mb-1"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          Бизнес
                        </div>
                        <div className="text-2xl font-bold text-yellow-600">
                          от 95,000₸
                        </div>
                        <div className="text-sm text-yellow-600">за м²</div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Демонтаж */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Демонтаж старых покрытий
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>
                  {/* Перегородки */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Возведение перегородок
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>
                  {/* Коммуникации */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Электрика и сантехника
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>
                  {/* Стяжка */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Стяжка пола, штукатурка стен
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>

                  {/* Закуп материалов */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Закуп материалов и доставка
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>

                  {/* Организация логистики */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Организация логистики
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>

                  {/* Покраска */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Покраска стен и потолков
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-close-line text-2xl text-gray-400"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>

                  {/* Напольное покрытие */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Укладка ламината/плитки
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-close-line text-2xl text-gray-400"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>

                  {/* Двери */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Межкомнатные двери
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-close-line text-2xl text-gray-400"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>

                  {/* Сантехника */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Сантехника и светильники
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-close-line text-2xl text-gray-400"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-purple-600"></i>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-yellow-600"></i>
                    </td>
                  </tr>

                  {/* Потолки */}
                  <tr className="border-b border-gray-100">
                    <td className="p-4 font-medium text-gray-800">
                      Натяжные потолки
                    </td>
                    <td className="p-4 text-center bg-green-50 border-l border-gray-200">
                      <i className="ri-close-line text-2xl text-gray-400"></i>
                    </td>
                    <td className="p-4 text-center bg-blue-50 border-l border-gray-200">
                      <i className="ri-check-line text-2xl text-blue-600"></i>
                    </td>
                    <td className="p-4 text-center bg-purple-50 border-l border-gray-200">
                      <div className="flex flex-col items-center">
                        <i className="ri-vip-crown-2-line text-2xl text-purple-600"></i>
                        <span className="text-xs text-purple-600 font-medium">
                          Многоуровневые
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-center bg-yellow-50 border-l border-gray-200">
                      <div className="flex flex-col items-center">
                        <i className="ri-vip-crown-2-line text-2xl text-yellow-600"></i>
                        <span className="text-xs text-yellow-600 font-medium">
                          Многоуровневые
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid lg:grid-cols-4 gap-6">
            <button
              onClick={scrollToQuiz}
              className="w-full py-4 rounded-2xl font-bold text-white bg-green-600 hover:bg-green-700 transition-all cursor-pointer text-lg"
            >
              Выбрать Демонтаж - от 30,000₸/м²
            </button>
            <button
              onClick={scrollToQuiz}
              className="w-full py-4 rounded-2xl font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all cursor-pointer text-lg relative"
            >
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                <div className="bg-yellow-400 text-blue-800 px-3 py-1 rounded-full text-xs font-bold">
                  ЧАСТО ВЫБИРАЕМЫЙ
                </div>
              </div>
              Выбрать Стандарт - от 55,000₸/м²
            </button>
            <button
              onClick={scrollToQuiz}
              className="w-full py-4 rounded-2xl font-bold text-white bg-purple-600 hover:bg-purple-700 transition-all cursor-pointer text-lg"
            >
              Выбрать Комфорт - от 75,000₸/м²
            </button>
            <button
              onClick={scrollToQuiz}
              className="w-full py-4 rounded-2xl font-bold text-white bg-yellow-600 hover:bg-yellow-700 transition-all cursor-pointer text-lg"
            >
              Выбрать Бизнес - от 95,000₸/м²
            </button>
          </div>

          {/* Bottom Section */}
          <div className="text-center mt-8">
            <div className="bg-yellow-50 rounded-2xl p-6 border-2 border-yellow-200">
              <h4
                className="text-lg font-bold mb-3"
                style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
              >
                💡 Не знаете какой пакет выбрать?
              </h4>
              <p className="text-gray-700 mb-4">
                Пройдите быстрый квиз — мы поможем определить оптимальный вариант под ваши потребности и бюджет
              </p>
              <button
                onClick={scrollToQuiz}
                className="px-8 py-3 rounded-2xl font-semibold text-white cursor-pointer hover:shadow-lg transition-all"
                style={{ backgroundColor: '#D69A00' }}
              >
                Пройти квиз и выбрать пакет
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About / Promise Section - мобильная адаптация */}
      <section className="py-12 lg:py-20 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <h2
              className="text-2xl lg:text-3xl xl:text-4xl font-bold mb-4 lg:mb-6"
              style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
            >
              Решаем 3 главные проблемы ремонта
            </h2>
            <div className="max-w-4xl mx-auto">
              <p
                className="text-base lg:text-lg leading-relaxed mb-4 lg:mb-6"
                style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}
              >
                Работаем по системе, которая исключает основные риски и гарантирует результат
              </p>
              <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-8 lg:mt-12">
                <div
                  className="text-center p-4 lg:p-6 rounded-2xl shadow-lg"
                  style={{ backgroundColor: '#FFFFFF', border: '2px solid #1FD0CE' }}
                >
                  <div
                    className="w-12 lg:w-16 h-12 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#1FD0CE' }}
                  >
                    <i className="ri-time-line text-xl lg:text-2xl text-white"></i>
                  </div>
                  <h3
                    className="text-lg lg:text-xl font-semibold mb-2 lg:mb-4"
                    style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                  >
                    Точно в срок
                  </h3>
                  <p
                    className="text-sm lg:text-base"
                    style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}
                  >
                    Никаких задержек — работаем по календарному плану
                  </p>
                </div>

                <div
                  className="text-center p-4 lg:p-6 rounded-2xl shadow-lg"
                  style={{ backgroundColor: '#FFFFFF', border: '2px solid #D69A00' }}
                >
                  <div
                    className="w-12 lg:w-16 h-12 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#D69A00' }}
                  >
                    <i className="ri-money-dollar-circle-line text-xl lg:text-2xl text-white"></i>
                  </div>
                  <h3
                    className="text-lg lg:text-xl font-semibold mb-2 lg:mb-4"
                    style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                  >
                    Точно в бюджете
                  </h3>
                  <p
                    className="text-sm lg:text-base"
                    style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}
                  >
                    Фиксированный бюджет — никаких доплат в процессе
                  </p>
                </div>

                <div
                  className="text-center p-4 lg:p-6 rounded-2xl shadow-lg"
                  style={{ backgroundColor: '#FFFFFF', border: '2px solid #22262B' }}
                >
                  <div
                    className="w-12 lg:w-16 h-12 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#22262B' }}
                  >
                    <i className="ri-shield-check-line text-xl lg:text-2xl text-white"></i>
                  </div>
                  <h3
                    className="text-lg lg:text-xl font-semibold mb-2 lg:mb-4"
                    style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
                  >
                    Высокое качество
                  </h3>
                  <p
                    className="text-sm lg:text-base"
                    style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}
                  >
                    Многоуровневый контроль и гарантии от 12 месяцев
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section - мобильная адаптация */}
      <section className="py-12 lg:py-20 px-4" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-2xl lg:text-3xl xl:text-4xl font-bold text-center mb-4 lg:mb-6"
            style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
          >
            Полный комплекс работ
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            <div className="bg-white p-4 lg:p-6 rounded-2xl shadow-lg text-center">
              <div
                className="w-12 lg:w-16 h-12 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#D69A00' }}
              >
                <i className="ri-pencil-ruler-2-line text-xl lg:text-2xl text-white"></i>
              </div>
              <h3
                className="font-semibold text-base lg:text-lg mb-2"
                style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
              >
                Планировка
              </h3>
              <p
                style={{ fontFamily: 'Inter, sans-serif', color: '#22262B', fontSize: '14px' }}
              >
                Планировка и дизайн под ваши задачи
              </p>
            </div>

            <div className="bg-white p-4 lg:p-6 rounded-2xl shadow-lg text-center">
              <div
                className="w-12 lg:w-16 h-12 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#D69A00' }}
              >
                <i className="ri-hammer-line text-xl lg:text-2xl text-white"></i>
              </div>
              <h3
                className="font-semibold text-base lg:text-lg mb-2"
                style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
              >
                Строительные работы
              </h3>
              <p
                style={{ fontFamily: 'Inter, sans-serif', color: '#22262B', fontSize: '14px' }}
              >
                От демонтажа до финишной отделки
              </p>
            </div>

            <div className="bg-white p-4 lg:p-6 rounded-2xl shadow-lg text-center">
              <div
                className="w-12 lg:w-16 h-12 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#D69A00' }}
              >
                <i className="ri-flashlight-line text-xl lg:text-2xl text-white"></i>
              </div>
              <h3
                className="font-semibold text-base lg:text-lg mb-2"
                style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
              >
                Инженерные системы
              </h3>
              <p
                style={{ fontFamily: 'Inter, sans-serif', color: '#22262B', fontSize: '14px' }}
              >
                Электрика, сантехника, вентиляция
              </p>
            </div>

            <div className="bg-white p-4 lg:p-6 rounded-2xl shadow-lg text-center sm:col-span-2 lg:col-span-1">
              <div
                className="w-12 lg:w-16 h-12 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#D69A00' }}
              >
                <i className="ri-truck-line text-xl lg:text-2xl text-white"></i>
              </div>
              <h3
                className="font-semibold text-base lg:text-lg mb-2"
                style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
              >
                Материалы
              </h3>
              <p
                style={{ fontFamily: 'Inter, sans-serif', color: '#22262B', fontSize: '14px' }}
              >
                Закупка и доставка всех материалов
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Block 1 - Quality Control */}
      <section className="py-12 lg:py-20 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-6xl mx-auto">
          <div className="rounded-2xl p-6 lg:p-8 xl:p-12 text-white shadow-2xl" style={{ background: 'linear-gradient(135deg, #D69A00 0%, #B8820A 100%)' }}>
            <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
              <div>
                <h2
                  className="text-2xl lg:text-3xl xl:text-4xl font-bold mb-4 lg:mb-6"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Наш опыт в цифрах
                </h2>
                <p
                  className="text-lg lg:text-xl mb-6 lg:mb-8 opacity-90"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Результаты работы за 5 лет в Костанае
                </p>
                <div className="grid grid-cols-2 gap-4 lg:gap-6">
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold mb-1 lg:mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>180+</div>
                    <div className="opacity-90 text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>Объектов сдано</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold mb-1 lg:mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>96%</div>
                    <div className="opacity-90 text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>Довольных клиентов</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold mb-1 lg:mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>24ч</div>
                    <div className="opacity-90 text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>Готовность сметы</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold mb-1 lg:mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>12+</div>
                    <div className="opacity-90 text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>Мес. гарантии</div>
                  </div>
                </div>
              </div>
              <div className="relative order-first lg:order-last">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Kazakhstan%20construction%20foreman%20team%20leader%20in%20work%20uniform%20and%20hard%20hat%20at%20renovation%20site%20in%20Kostanay%20city%20with%20experienced%20building%20supervisor%20with%20construction%20tools%20and%20blueprints&width=500&height=400&seq=foreman-leader-expert5&orientation=landscape"
                  alt="Бригадир BALAD"
                  className="rounded-2xl shadow-lg object-cover object-top w-full h-64 lg:h-80"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Block 2 - Quality Control */}
      <section className="py-12 lg:py-20 px-4" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <h2
              className="text-2xl lg:text-3xl xl:text-4xl font-bold mb-3 lg:mb-4"
              style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}
            >
              Система контроля качества
            </h2>
            <p className="text-lg lg:text-xl max-w-4xl mx-auto" style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}>
              Многоуровневая проверка на каждом этапе работ
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-last lg:order-first">
              <div className="space-y-4 lg:space-y-6">
                <div className="flex items-start space-x-3 lg:space-x-4">
                  <div className="w-10 lg:w-12 h-10 lg:h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D69A00' }}>
                    <i className="ri-shield-check-line text-lg lg:text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-base lg:text-lg font-semibold mb-1 lg:mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}>
                      Приемка каждого этапа
                    </h3>
                    <p className="text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}>
                      Проверка качества работ перед переходом к следующему этапу
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 lg:space-x-4">
                  <div className="w-10 lg:w-12 h-10 lg:h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1FD0CE' }}>
                    <i className="ri-camera-line text-lg lg:text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-base lg:text-lg font-semibold mb-1 lg:mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}>
                      Фото-отчеты
                    </h3>
                    <p className="text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}>
                      Еженедельные отчеты о ходе работ с подробными фотографиями
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 lg:space-x-4">
                  <div className="w-10 lg:w-12 h-10 lg:h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#22262B' }}>
                    <i className="ri-time-line text-lg lg:text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-base lg:text-lg font-semibold mb-1 lg:mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#22262B' }}>
                      Соблюдение сроков
                    </h3>
                    <p className="text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif', color: '#22262B' }}>
                      Календарное планирование с учетом технологических перерывов
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative order-first lg:order-last">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Kazakhstan%20construction%20foreman%20with%20tablet%20documenting%20high%20quality%20renovation%20work%20in%20beautiful%20apartment%20interior%20with%20detailed%20quality%20inspection%20checklist%2C%20professional%20standards%20verification%20in%20Kostanay%20residential%20project&width=600&height=500&seq=quality-documentation3&orientation=landscape"
                  alt="Контроль качества"
                  className="rounded-2xl shadow-lg object-cover object-top w-full h-64 lg:h-96"
                />
                <div className="absolute bottom-6 lg:bottom-6 left-6 lg:left-6 text-white p-3 lg:p-4 rounded-2xl shadow-lg" style={{ backgroundColor: '#D69A00' }}>
                  <div className="text-base lg:text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>100%</div>
                  <div className="text-xs lg:text-sm opacity-90" style={{ fontFamily: 'Inter, sans-serif' }}>Контроль качества</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - мобильная адаптация */}
      <footer className="bg-gray-900 text-white py-12 lg:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8 mb-8 lg:mb-12">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <h3
                className="text-xl lg:text-2xl font-bold mb-3 lg:mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                QOSTANAY Renovation
              </h3>
              <p
                className="text-gray-300 mb-4 lg:mb-6 leading-relaxed text-sm lg:text-base"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Профессиональная ремонтная компания в Костанае. Ремонт квартир, офисов и коммерческих помещений.
                Гарантируем точные сроки, фиксированный бюджет и высокое качество работ.
              </p>
              <div className="space-y-2 lg:space-y-3">
                <div className="flex items-center space-x-2 lg:space-x-3">
                  <i className="ri-whatsapp-line text-yellow-400"></i>
                  <button
                    onClick={handleWhatsAppClick}
                    className="cursor-pointer hover:text-yellow-400 transition-colors text-sm lg:text-base"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    WhatsApp: +7 (700) 263-67-81
                  </button>
                </div>
                <div className="flex items-center space-x-2 lg:space-x-3">
                  <i className="ri-map-pin-line text-yellow-400"></i>
                  <span className="text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                    г. Костанай, Казахстан
                  </span>
                </div>
                <div className="flex items-center space-x-2 lg:space-x-3">
                  <i className="ri-time-line text-yellow-400"></i>
                  <span className="text-sm lg:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Пн-Вс: 08:00-20:00
                  </span>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg lg:text-lg font-semibold mb-3 lg:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Услуги
              </h4>
              <ul className="space-y-1 lg:space-y-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                <li>
                  <button onClick={scrollToQuiz} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Ремонт квартир
                  </button>
                </li>
                <li>
                  <button onClick={scrollToQuiz} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Ремонт офисов
                  </button>
                </li>
                <li>
                  <button onClick={() => alert('Дизайн-проекты не делаем, но можем порекомендовать партнёров. Свяжитесь с нами!')} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Консультация по дизайну
                  </button>
                </li>
                <li>
                  <button onClick={scrollToQuiz} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Черновая отделка
                  </button>
                </li>
                <li>
                  <button onClick={scrollToQuiz} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Чистовая отделка
                  </button>
                </li>
                <li>
                  <button onClick={scrollToQuiz} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Инженерные системы
                  </button>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg lg:text-lg font-semibold mb-3 lg:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Быстрые ссылки
              </h4>
              <ul className="space-y-1 lg:space-y-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                <li>
                  <button onClick={scrollToQuiz} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Калькулятор стоимости
                  </button>
                </li>
                <li>
                  <button onClick={handleWhatsAppClick} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Написать в WhatsApp
                  </button>
                </li>
                <li>
                  <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    О компании
                  </button>
                </li>
                <li>
                  <button onClick={scrollToQuiz} className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer text-sm lg:text-base">
                    Портфолио
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-700 pt-6 lg:pt-8">
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-3 lg:space-y-0 lg:space-x-3">
              <div className="text-gray-400 text-sm lg:text-sm text-center lg:text-left">
                © 2024 QOSTANAY Renovation. Все права защищены.
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-6 text-sm lg:text-sm">
                <button onClick={() => alert('Страница в разработке')} className="text-gray-400 hover:text-yellow-400 transition-colors cursor-pointer">
                  Политика конфиденциальности
                </button>
                <button onClick={() => alert('Страница в разработке')} className="text-gray-400 hover:text-yellow-400 transition-colors cursor-pointer">
                  Пользовательское соглашение
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
